/**
 * Delays React's takeover on a prerendered route until that route's lazy chunk
 * is loaded.
 *
 * THE PROBLEM. `/privacy` and `/terms` now arrive with the full policy in the
 * HTML, but their components are `React.lazy` chunks. React clears `#root` on
 * its first commit, and on that commit the chunk has not arrived — so the
 * visitor read the policy, watched it be replaced by the Suspense shimmer, and
 * got it back a moment later. Measured at 2-4 dropped frames (~210-225ms at 6x
 * CPU throttle) and as long as the download takes on a cold mobile connection.
 * These are the two pages an external reviewer is most likely to open, and a
 * page that flickers its own legal text away reads as broken.
 *
 * WHY NOT THE OBVIOUS FIXES. Preloading the chunk from the page's HTML was
 * measured and did NOT close the gap (2-3 frames, statistically the same as
 * without it) — the fallback commits whether or not the bytes are local, so the
 * preload only shortens the download, not the flicker. Importing the two pages
 * statically does close it, and costs 12.8KB gzipped on a 258KB main bundle —
 * a 5% regression on every route for every visitor, to fix two rarely-visited
 * pages. Both were rejected on measurements, not taste.
 *
 * WHAT THIS DOES INSTEAD. Nothing is faster here; the ORDER changes. The
 * prerendered markup is already on screen and already readable, so the useful
 * thing to do with the chunk-loading window is to spend it showing that markup
 * rather than a shimmer. This loads the route's module before React renders and
 * hands it to the route synchronously.
 *
 * AWAITING THE CHUNK IS NOT SUFFICIENT — MEASURED. Delaying `root.render()`
 * until the import resolves still showed the shimmer for 2-3 frames, because
 * `React.lazy` suspends on its FIRST read whether or not the module is in
 * webpack's cache: it calls the factory, gets a promise, throws it, and retries
 * after a microtask, and at 6x throttle that retry lands after a paint.
 * Instrumenting the dropped frames is what showed this — `#root` held the
 * route announcer plus `RouteShellFallback`, not an empty container. So the
 * module is also published here, and src/App.js renders it directly (bypassing
 * `lazy`) when it is present. `lazy` remains the path for every other visitor.
 *
 * FAILURE IS NOT AN OPTION HERE, so there is no failure path that blocks boot:
 * a rejected import, a missing chunk, or a slow network all fall through to
 * rendering anyway, on a timer, and the route falls back to `lazy` + Suspense
 * exactly as it does today. The worst case is today's behaviour.
 */

import type React from 'react';

/**
 * How long React's takeover may wait. Past this the app boots and Suspense
 * handles the rest exactly as it does today — a flicker is a far better outcome
 * than a page that never becomes interactive. Generous enough to cover a 3G
 * chunk fetch, short enough that a visitor who wants to click something is not
 * left reading static markup.
 */
const MAX_WAIT_MS = 2500;

/**
 * Routes that ship a prerendered body AND load their component from a lazy
 * chunk. The homepage is absent on purpose: LandingPage is statically imported,
 * which is why its measured handover has no gap and why it needs no waiting.
 *
 * The chunk names must match src/App.js — that is what makes this the same
 * module rather than a second copy of it. `scripts/og/bodyRoutes.js` names the
 * same two chunks for its preload, and injectBody.test.ts asserts all three
 * places agree.
 */
const PRERENDERED_LAZY_ROUTES: Record<string, () => Promise<unknown>> = {
  '/privacy': () => import(/* webpackChunkName: "legal-privacy" */ '../theaWeb/pages/PrivacyPage'),
  '/terms': () => import(/* webpackChunkName: "legal-terms" */ '../theaWeb/pages/TermsPage'),
};

/** Normalizes `/privacy`, `/privacy/` and `/Privacy/` to one key. */
export function prerenderedRouteKey(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '').toLowerCase();
  return trimmed === '' ? '/' : trimmed;
}

/** Modules loaded before the first render, by route key. */
const preloaded = new Map<string, { default: React.ComponentType }>();

/**
 * The route's component if it was loaded before React's first render, else
 * undefined. src/App.js renders this directly instead of going through
 * `React.lazy`, which is what removes the one-commit shimmer; see the module
 * comment above for why awaiting the chunk alone does not.
 */
export function preloadedRouteComponent(pathname: string): React.ComponentType | undefined {
  return preloaded.get(prerenderedRouteKey(pathname))?.default;
}

/**
 * Resolves when it is time to render: immediately on any route that is not a
 * prerendered lazy one, otherwise once the chunk has settled or MAX_WAIT_MS has
 * passed, whichever comes first. Never rejects.
 */
export function awaitPrerenderedRouteChunk(pathname: string): Promise<void> {
  const key = prerenderedRouteKey(pathname);
  const load = PRERENDERED_LAZY_ROUTES[key];
  if (!load) return Promise.resolve();

  return Promise.race([
    // Both settlements resolve: a chunk that fails to load must still let the
    // app boot, where the existing ErrorBoundary and Suspense handle it the way
    // they do on every other route. On failure nothing is published, so the
    // route takes the `lazy` path and behaves exactly as it does today.
    load().then(
      (mod) => {
        const resolved = mod as { default?: React.ComponentType };
        if (typeof resolved?.default === 'function') {
          preloaded.set(key, { default: resolved.default });
        }
      },
      () => undefined,
    ),
    new Promise<void>((resolve) => {
      setTimeout(resolve, MAX_WAIT_MS);
    }),
  ]);
}

/** Test seam: forget anything published by a previous render. */
export function resetPreloadedRouteComponents(): void {
  preloaded.clear();
}

export const PRERENDERED_LAZY_ROUTE_PATHS = Object.keys(PRERENDERED_LAZY_ROUTES);
export const PRERENDER_CHUNK_MAX_WAIT_MS = MAX_WAIT_MS;
